<template>
    <div>       
        <v-card
            class="mx-auto"
            width="380"
            elevation="0"
            :loading="props.rewardLoading"
        >
        <template v-slot:loader="{ isActive  }">
            <v-progress-linear
                :active="isActive"
                color="deep-purple"
                height="4"
                indeterminate
            ></v-progress-linear>
        </template>

            <v-card-title>
                {{ props.title }}
            </v-card-title>

            <v-card-subtitle>
                <p>
                    <v-icon size="xx-small" :color=chartOptions.colors[0]>mdi-square</v-icon> {{ props.subTitleLine4 }}<br/>
                    <v-icon size="xx-small" :color=chartOptions.colors[1]>mdi-square</v-icon> {{ props.subTitleLine3 }}<br/>
                    <v-icon size="xx-small" :color=chartOptions.colors[2]>mdi-square</v-icon> {{ props.subTitleLine2 }}<br/>
                    <v-icon size="xx-small" :color=chartOptions.colors[3]>mdi-square</v-icon> {{ props.subTitleLine1 }}<br/>
                </p>
                
            </v-card-subtitle>

            <v-spacer></v-spacer>

            <v-chip size="small" 
                color="light-gray-darken-1" 
                variant="outlined"
                style="top:50%; transform: translate(10%, 50%);">
                Total Eligible Units: {{ props.totalUnits > 0 ? props.totalUnits : 0 }}
            </v-chip>

            <v-chip size="small" 
                variant="outlined" 
                :color="thresholdAchievedColor"
                style="top:50%; transform: translate(15%, 50%);">
                <b>Unit Count Towards Threshold: {{ props.units > 0 ? props.units : 0 }}</b>
            </v-chip>                

            <v-spacer></v-spacer>

            <div id="chart" style="position: relative;">

                <div style="position: absolute; top:45%; left:20%; transform: translate(-50%, -50%); font-size: x-small; z-index:1000">
                    <v-chip size="x-small" :color="thresholdAchievedColor" variant="outlined" style="top:50%; left:50%; transform: translate(-50%, 50%);">
                    <b>&nbsp;{{ thresholdAchieved }}&nbsp;</b> 
                    </v-chip>
                </div>
                <div style="position: absolute; top:37%; left:30%; transform: translate(-50%, -50%); font-size: x-small;">
                    <v-chip size="x-small" :color="thresholdNextColor" variant="outlined" style="top:50%; left:50%; transform: translate(-50%, 50%);">
                    <b>&nbsp;{{ unitCountToNextThreshold }} More Eligible Units To Next Threshold&nbsp;</b> 
                    </v-chip>
                </div>

                <apexchart type="radialBar" height="420" :options="chartOptions" :series="series"></apexchart>
            </div>

        </v-card>
        
    </div>
</template>

<script setup>
import { ref, onMounted,  watch } from 'vue'

const props = defineProps({
  title: { type: String, default: '' },
  subTitleLine1: { type: String, default: '' },
  subTitleLine2: { type: String, default: '' },
  subTitleLine3: { type: String, default: '' },
  subTitleLine4: { type: String, default: '' },
  labels: { type: Array, default: [] },
  units: { type: Number, default: 0 },
  totalUnits: { type: Number, default: 0 },
  limit1: { type: Number, default: 0 },
  limit2: { type: Number, default: 0 },
  limit3: { type: Number, default: 0 },
  limit4: { type: Number, default: 0},
  rewardLoading: { type: Boolean, default: false}
})

const colors = ref(['#1ab7ea', '#0084ff', '#39539E', '#005D8F'])
let thresholdAchievedColor = ref('')
let thresholdNextColor = ref('')
let thresholdAchieved = ref('')
let unitCountToNextThreshold = ref(0)
 
const chartOptions = {
    chart: {
        height: 390,
        type: 'radialBar',
    },
    plotOptions: {
        radialBar: {
        offsetY: 0,
        startAngle: 0,
        endAngle: 270,
        hollow: {
            margin: 5,
            size: '30%',
            background: 'transparent',
            image: undefined,
        },
        dataLabels: {
            name: {
            show: false,
            },
            value: {
            show: false,
            }
        },
        barLabels: {
            enabled: true,
            useSeriesColors: true,
            offsetX: -8,
            fontSize: '12px',
            formatter: function(seriesName, opts) {
            return seriesName + ":  " + String(opts.w.globals.series[opts.seriesIndex]).padStart(3, ' ') + "%"
            },
        },
        }
    },
    colors: colors.value,
    labels: props.labels,
    responsive: [{
        breakpoint: 480,
        options: {
        legend: {
            show: false
        }
        }
    }]
}

let series = ref([0, 0, 0, 0])

const reCalc = (units, limit1, limit2, limit3, limit4) => {

    if (units < 0) {
        units = 0
    }
    
    let t4 = Math.round(units / limit4 * 100, 0)
    let t3 = Math.round(units / limit3 * 100, 0)
    let t2 = Math.round(units / limit2 * 100, 0)
    let t1 = Math.round(units / limit1 * 100, 0)

    thresholdAchieved.value = 'Threshold Not Achieved'

    if (units >= 0) {
        thresholdNextColor.value = colors.value[3]
    }
    unitCountToNextThreshold.value = limit1 - units    
    
    if (t1 >= 100) {
        t1 = 100
        thresholdAchievedColor.value = colors.value[3]
        thresholdNextColor.value = colors.value[2]
        thresholdAchieved.value = 'Threshold 1 Achieved'
        unitCountToNextThreshold.value = limit2 - units
    }    
    if (t2 >= 100) {
        t2 = 100
        thresholdAchievedColor.value = colors.value[2]
        thresholdNextColor.value = colors.value[1]
        thresholdAchieved.value = 'Threshold 2 Achieved'
        unitCountToNextThreshold.value = limit3 - units
    }
    if (t3 >= 100) {
        t3 = 100
        thresholdAchievedColor.value = colors.value[1]
        thresholdNextColor.value = colors.value[0]
        thresholdAchieved.value = 'Threshold 3 Achieved'
        unitCountToNextThreshold.value = limit4 - units
    }
    if (t4 >= 100) {
        t4 = 100
        thresholdAchievedColor.value = colors.value[0]
        thresholdNextColor.value = colors.value[0]
        thresholdAchieved.value = 'Threshold 4 Achieved'
        unitCountToNextThreshold.value = 0
    }

    if (t1 < 0) {
        t1 = 0
    }
    if (t2 < 0) {
        t2 = 0
    }
    if (t3 < 0) {
        t3 = 0
    }
    if (t4 < 0) {
        t4 = 0
    }
    if (unitCountToNextThreshold.value < 0) {
        unitCountToNextThreshold.value = 0
    }

    series.value[0] = t4
    series.value[1] = t3
    series.value[2] = t2
    series.value[3] = t1
}

watch(
    () => props.units,
    (val) => {
        reCalc(val, props.limit1, props.limit2, props.limit3, props.limit4)
    },
    {immediate:true}
)

watch(
    () => props.limit1,
    (val) => {
        reCalc(props.units, val, props.limit2, props.limit3, props.limit4)
    },
    {immediate:true}
)

watch(
    () => props.limit2,
    (val) => {
        reCalc(props.units, props.limit1, val, props.limit3, props.limit4)
    },
    {immediate:true}
)

watch(
    () => props.limit3,
    (val) => {
        reCalc(props.units, props.limit1, props.limit2, val, props.limit4)
    },
    {immediate:true}
)

watch(
    () => props.limit4,
    (val) => {
        reCalc(props.units, props.limit1, props.limit2, props.limit3, val)
    },
    {immediate:true}
)

onMounted(() => {
})


</script>
